<template>
  <!-- Invisible anchor positioned next to the invoking element; the member
       picker dropdown (a Tippy teleported to #theme) opens against it. -->
  <div class="assignee-picker-anchor" :style="anchorStyle">
    <Suspense>
      <GraphRelation
        v-if="target"
        :key="invocation"
        ref="relationEl"
        state="inline"
        :name="target.fieldName"
        :type="target.relationType"
        :data="data"
        :object-id="target.rowId"
        :database="target.database"
        optional
        :show-collection="false"
        :show-checkmark="false"
        clear-choice-label="Unassigned"
      >
        <template #clearChoiceIcon>
          <PersonIcon />
        </template>
        <template #btn>
          <span class="assignee-picker-trigger" />
        </template>
      </GraphRelation>
    </Suspense>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  ref,
  shallowRef,
  watch,
} from "vue";

import type { GeneralizedTask } from "@ctrl/controller-rpc";

import type { GraphRelationResult } from "@/components/graph/graph-relation";
import GraphRelation from "@/components/graph/properties/GraphRelation.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import * as inject from "@/components/inject";
import {
  memberRelationResult,
  taskAssignTarget,
  type TaskAssignTarget,
} from "@/components/task/assignee";

/**
 * App-wide assignee picker (mounted once by Routine.vue, like `DatePicker`):
 * `showPicker(triggerEl, task)` opens the member picker of a bound task's
 * member relation cell next to `triggerEl`. It reuses `GraphRelation` (the
 * same editor as `AssigneeButton`), whose autosave writes the selection to
 * the task's bound row. No-ops with a notification when the task is not
 * assignable (see `taskAssignTarget`).
 */
export default defineComponent({
  components: { GraphRelation, PersonIcon },
  setup() {
    const controller = inject.controller();
    const target = shallowRef<TaskAssignTarget | null>(null);
    // Bumped on every `showPicker` so the `GraphRelation` remounts with the
    // freshly resolved task data.
    const invocation = ref(0);
    const anchorStyle = ref<Record<string, string>>({});
    const relationEl = ref<InstanceType<typeof GraphRelation>>();
    const pendingOpen = ref(false);

    const data = computed<GraphRelationResult | undefined>(() =>
      target.value
        ? memberRelationResult(target.value.assignee, target.value.relationType)
        : undefined
    );

    // The `GraphRelation` mounts asynchronously (Suspense) and only renders
    // its dropdown once its choices are loaded: open as soon as both are
    // there.
    watch(
      () => (pendingOpen.value ? relationEl.value?.choices : undefined),
      async (choices) => {
        if (!choices || !relationEl.value) return;
        pendingOpen.value = false;
        await nextTick();
        // Re-read after the tick: the instance may have been replaced.
        const relation = relationEl.value;
        if (relation && !relation.isPopupOpen()) relation.togglePopup();
      }
    );

    const showPicker = async (
      triggerEl: HTMLElement | undefined,
      task: GeneralizedTask
    ) => {
      const resolved = await taskAssignTarget(controller, task);
      if (!resolved) {
        controller.notifications.notify("This task cannot be assigned");
        return;
      }
      const rect = triggerEl?.getBoundingClientRect();
      // Tear down any previous `GraphRelation` before arming `pendingOpen`:
      // on repeat invocations the watch above would otherwise fire against
      // the previous, already-loaded instance — right before the key change
      // destroys it — and its `togglePopup()` would no-op on a destroyed
      // tippy, eating the invocation.
      pendingOpen.value = false;
      target.value = null;
      await nextTick();
      anchorStyle.value = rect
        ? { top: `${rect.bottom}px`, left: `${rect.left}px` }
        : { top: "50%", left: "50%" };
      invocation.value += 1;
      target.value = resolved;
      pendingOpen.value = true;
    };

    return {
      anchorStyle,
      data,
      invocation,
      relationEl,
      showPicker,
      target,
    };
  },
});
</script>

<style scoped lang="scss">
.assignee-picker-anchor {
  position: fixed;
  z-index: $z-index-modal;
  width: 0;
  height: 0;
  pointer-events: none;
  overflow: hidden;
}
</style>
